import React, { useEffect, useRef } from "react";

import { IBooleanField, ValidationTypes } from "../../models/formFields";
import { notEmpty, unique } from "../../utils/functional";
import { FormInputFieldWrapper } from "../FormInputFieldWrapper";
import { useFormField } from "../hooks/useFormField";
import { runBooleanValidator } from "../validation";

export const FormCheckbox = <T extends string>(
    props: IBooleanField<T>,
): React.ReactNode => {
    const { type = "checkbox", checked, labelCSSClass, label, hasHTML } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    // Custom validators for boolean fields
    const runBooleanValidators = (): string[] => {
        if (!props.validation) {
            return [];
        }
        const value = checked || false;
        const validators = ([] as ValidationTypes[]).concat(props.validation);

        const errors = validators
            .filter(unique)
            .map((validator) => {
                return runBooleanValidator(validator, value);
            })
            .filter(notEmpty);
        if (props.onValidStateChange) {
            props.onValidStateChange(props.name, errors);
        }
        return errors;
    };

    const didUpdateRef = useRef(false);

    // Run validators when checked changes (after the initial mount)
    useEffect(() => {
        if (!didUpdateRef.current) {
            didUpdateRef.current = true;
            return;
        }
        runBooleanValidators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checked]);

    const inputProps = {
        ...field.inputProps,
        checked,
    };

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
            label={undefined}
            placeholder={undefined}
        >
            <label className={labelCSSClass}>
                <input ref={field.inputRef} {...inputProps} />
                {label && hasHTML && (
                    <span dangerouslySetInnerHTML={{ __html: label }} />
                )}
                {!hasHTML ? label : null}
            </label>
        </FormInputFieldWrapper>
    );
};
